import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'About', path: '/about' },
  { label: 'Tampa Bay', path: '/location' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    document.body.style.overflow = '';
  }, [location.pathname]);

  const toggleMobile = () => {
    const next = !mobileOpen;
    setMobileOpen(next);
    document.body.style.overflow = next ? 'hidden' : '';
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-white ${
          scrolled
            ? 'shadow-sm border-b border-gray-100'
            : 'border-b border-gray-100/60'
        }`}
        style={{ height: scrolled ? '72px' : '88px' }}
      >
        <div className="h-full mx-auto px-6 lg:px-12 xl:px-20 flex items-center justify-between transition-all duration-500">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 relative z-10">
            <img
              src="https://www.selightingdesign.com/wp-content/uploads/2022/01/Southeast-Lighting-Design-Logo-cropped-scaled.jpg"
              alt="Southeast Lighting & Design"
              className={`w-auto object-contain transition-all duration-500 ${scrolled ? 'h-10' : 'h-12'}`}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-[10px] font-semibold tracking-[0.28em] uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap group ${
                  location.pathname === link.path
                    ? 'text-gold'
                    : 'text-charcoal/60 hover:text-charcoal'
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-px bg-gold transition-all duration-300 ${
                    location.pathname === link.path ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-6">
            <a
              href="tel:7279149002"
              className="flex items-center gap-2 text-charcoal/60 text-xs font-medium tracking-wider hover:text-gold transition-colors duration-300 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-gold text-sm"></i>
              </div>
              727-914-9002
            </a>
            <Link
              to="/services"
              className="whitespace-nowrap bg-gold text-charcoal text-[10px] font-bold tracking-[0.22em] uppercase px-6 py-3 hover:bg-gold-light transition-colors duration-300 cursor-pointer"
            >
              Get a Quote
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={toggleMobile}
            className="md:hidden w-10 h-10 flex items-center justify-center text-charcoal cursor-pointer"
            aria-label="Toggle menu"
          >
            <div className="w-6 flex flex-col gap-1.5">
              <span className={`block h-px bg-charcoal transition-all duration-300 ${mobileOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`block h-px bg-charcoal transition-all duration-300 ${mobileOpen ? 'opacity-0' : ''}`} />
              <span className={`block h-px bg-charcoal transition-all duration-300 ${mobileOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </div>
          </button>
        </div>
      </header>

      {/* Mobile menu overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-white flex flex-col pt-24 pb-12 px-8">
          <nav className="flex flex-col gap-1">
            {NAV_LINKS.map((link, i) => (
              <Link
                key={link.path}
                to={link.path}
                style={{ transitionDelay: `${i * 60}ms` }}
                className={`py-5 border-b border-gray-100 text-sm font-medium tracking-[0.22em] uppercase transition-colors cursor-pointer ${
                  location.pathname === link.path ? 'text-gold' : 'text-charcoal/60 hover:text-gold'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="mt-10 flex flex-col gap-4">
            <a
              href="tel:7279149002"
              className="flex items-center gap-3 text-gold text-sm font-semibold cursor-pointer"
            >
              <div className="w-10 h-10 flex items-center justify-center border border-gold/40">
                <i className="ri-phone-line text-gold"></i>
              </div>
              727-914-9002
            </a>
            <Link
              to="/services"
              className="bg-gold text-charcoal text-xs font-bold tracking-[0.22em] uppercase px-6 py-4 text-center whitespace-nowrap cursor-pointer"
            >
              Request a Free Assessment
            </Link>
          </div>
        </div>
      )}

      {/* Mobile sticky call bar */}
      <a
        href="tel:7279149002"
        className="md:hidden fixed bottom-0 left-0 right-0 bg-gold text-charcoal text-center py-4 text-xs font-bold tracking-[0.2em] uppercase whitespace-nowrap z-40 cursor-pointer flex items-center justify-center gap-2"
      >
        <i className="ri-phone-fill"></i>
        CALL NOW — 727-914-9002
      </a>
    </>
  );
}
